import { useEffect, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import FileAttachmentPanel from '../../../components/FileAttachmentPanel/FileAttachmentPanel';
import {
  getStudyStateTypes,
  uploadStudyFile,
  deleteStudyUploadedFile,
} from '../../../services/StudyResultsController';
import type {
  StudyStateTypeOption,
  UploadedFile,
  ExistingFile,
} from '../../../services/StudyResultsController';
import type { DeterminationRow } from './DeterminationsStep';

export interface ResultStepProps {
  stateTypeId: number | null;
  onStateTypeIdChange: (id: number | null) => void;
  resultDate: Date | null;
  onResultDateChange: (date: Date | null) => void;
  resultDescription: string;
  onResultDescriptionChange: (value: string) => void;
  resultText: string;
  onResultTextChange: (value: string) => void;
  uploadedFiles: UploadedFile[];
  onUploadedFilesChange: (files: UploadedFile[]) => void;
  existingFiles: ExistingFile[];
  determinations: DeterminationRow[];
}

/** Build a plain-text summary from determinations for pre-filling the result text. */
function buildDeterminationsText(determinations: DeterminationRow[]): string {
  if (determinations.length === 0) return '';
  const lines = determinations.map(
    (d) =>
      `${d.description}: ${d.value}${d.unit ? ' ' + d.unit : ''}`,
  );
  return 'Determinaciones:\n' + lines.join('\n');
}

export default function ResultStep({
  stateTypeId,
  onStateTypeIdChange,
  resultDate,
  onResultDateChange,
  resultDescription,
  onResultDescriptionChange,
  resultText,
  onResultTextChange,
  uploadedFiles,
  onUploadedFilesChange,
  existingFiles,
  determinations,
}: ResultStepProps) {
  const [stateTypes, setStateTypes] = useState<StudyStateTypeOption[]>([]);

  // Load study state types on mount
  useEffect(() => {
    getStudyStateTypes()
      .then((types) => {
        setStateTypes(types);
        // Default to "Informado completo" if not yet selected
        if (stateTypeId == null && types.length > 0) {
          const informado = types.find(
            (t) => t.name.toLowerCase().includes('informado completo'),
          );
          onStateTypeIdChange(informado ? informado.id : types[0].id);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill result text from determinations when entering this step
  // (only if resultText is currently empty and there are determinations)
  useEffect(() => {
    if (!resultText && determinations.length > 0) {
      onResultTextChange(buildDeterminationsText(determinations));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="result-step">
      {/* Form fields */}
      <div className="result-step__form">
        {/* State */}
        <div className="result-step__field">
          <label className="result-step__label">Estado *</label>
          <Dropdown
            value={stateTypeId}
            options={stateTypes}
            optionLabel="name"
            optionValue="id"
            onChange={(e) => onStateTypeIdChange(e.value)}
            placeholder="Seleccione estado"
            className="result-step__dropdown"
          />
        </div>

        {/* Result Date */}
        <div className="result-step__field">
          <label className="result-step__label">Fecha de resultado *</label>
          <Calendar
            value={resultDate}
            onChange={(e) => onResultDateChange(e.value as Date | null)}
            dateFormat="dd/mm/yy"
            showIcon
            placeholder="dd/mm/aaaa"
            className="result-step__calendar"
          />
        </div>

        {/* Description */}
        <div className="result-step__field result-step__field--full">
          <label className="result-step__label">Descripción *</label>
          <InputText
            value={resultDescription}
            onChange={(e) => onResultDescriptionChange(e.target.value)}
            placeholder="Descripción breve del resultado"
            className="result-step__input"
          />
        </div>

        {/* Result text */}
        <div className="result-step__field result-step__field--full">
          <label className="result-step__label">Texto del resultado</label>
          <InputTextarea
            value={resultText}
            onChange={(e) => onResultTextChange(e.target.value)}
            rows={6}
            autoResize
            placeholder="Detalle del resultado..."
            className="result-step__textarea"
          />
        </div>
      </div>

      {/* File upload */}
      <FileAttachmentPanel
        files={uploadedFiles}
        onFilesChange={onUploadedFilesChange}
        uploadFile={uploadStudyFile}
        deleteFile={deleteStudyUploadedFile}
        label="Archivos adjuntos del resultado"
        uploadLabel="Adjuntar archivo"
        emptyMessage="Sin archivos nuevos adjuntos"
      />

      {/* Existing files (read-only) */}
      {existingFiles.length > 0 ? (
        <div>
          <h4 className="result-step__section-title">Archivos existentes</h4>
          <div className="result-step__existing-files">
            {existingFiles.map((f) => (
              <div key={f.fileId} className="result-step__existing-file">
                <i className="pi pi-file" />
                <span>{f.fileName}{f.description ? ` - ${f.description}` : ''}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
